import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import CloseIcon from "@mui/icons-material/Close";

// ── animation variants ────────────────────────────────────────────────────────
const backdropVariants = {
  hidden:  { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.22 } },
  exit:    { opacity: 0, transition: { duration: 0.18 } },
};

const cardVariants = {
  hidden:  { opacity: 0, y: 18, scale: 0.97 },
  visible: { opacity: 1, y: 0,  scale: 1,   transition: { duration: 0.32, ease: [0.16, 1, 0.3, 1] } },
  exit:    { opacity: 0, y: 10, scale: 0.98, transition: { duration: 0.2 } },
};

// ── tag pill ──────────────────────────────────────────────────────────────────
const TAG_STYLES = {
  purple: { background: "rgba(128,24,231,0.14)", border: "1px solid rgba(128,24,231,0.45)", color: "#b87cff" },
  green:  { background: "rgba(16,185,129,0.12)",  border: "1px solid rgba(16,185,129,0.4)",  color: "#34d399" },
  blue:   { background: "rgba(59,130,246,0.12)",  border: "1px solid rgba(59,130,246,0.4)",  color: "#7cb8ff" },
  orange: { background: "rgba(249,115,22,0.13)",  border: "1px solid rgba(249,115,22,0.42)", color: "#fdba74" },
  pink:   { background: "rgba(236,72,153,0.13)",  border: "1px solid rgba(236,72,153,0.42)", color: "#f9a8d4" },
  teal:   { background: "rgba(20,184,166,0.13)",  border: "1px solid rgba(20,184,166,0.42)", color: "#5eead4" },
};

const Tag = ({ label, color = "blue" }) => (
  <span style={{
    ...(TAG_STYLES[color] || TAG_STYLES.blue),
    fontSize: 11, fontWeight: 600,
    fontFamily: "'Barlow', sans-serif",
    letterSpacing: "0.07em", textTransform: "uppercase",
    padding: "4px 12px", display: "inline-block",
  }}>
    {label}
  </span>
);

// ── section block ─────────────────────────────────────────────────────────────
const Section = ({ label, children }) => (
  <div style={{ marginBottom: 18 }}>
    <div style={{
      fontSize: 13, fontWeight: 600, letterSpacing: "0.11em",
      textTransform: "uppercase", color: "rgba(255,255,255,0.52)",
      fontFamily: "'Barlow', sans-serif", marginBottom: 8,
    }}>
      {label}
    </div>
    {children}
  </div>
);

// ── card base style (desktop only — mobile overridden by CSS class) ────────────
const gradBorderStyle = {
  position: "relative",
  background: "rgba(10, 10, 16, 0.98)",
  border: "1px solid rgba(255,255,255,0.1)",
  width: "100%",
  maxWidth: 540,
  padding: "28px 28px 22px",
  fontFamily: "'Barlow', sans-serif",
};

// ── pulsing lamp ──────────────────────────────────────────────────────────────
const Lamp = ({ color = "#10b981" }) => (
  <span style={{
    display: "inline-block", width: 7, height: 7,
    borderRadius: "50%", flexShrink: 0,
    background: color,
    boxShadow: `0 0 7px ${color}`,
    animation: "vt-lamp-pulse 2.4s ease-in-out infinite",
  }} />
);

// ── gradient brand text ───────────────────────────────────────────────────────
const BrandText = ({ children }) => (
  <span style={{
    background: "linear-gradient(to right, #10b981, #3b82f6)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  }}>
    {children}
  </span>
);

// ── keyframe + responsive CSS injection (once) ────────────────────────────────
let _stylesInjected = false;
function injectStyles() {
  if (_stylesInjected || typeof document === "undefined") return;
  _stylesInjected = true;
  const el = document.createElement("style");
  el.textContent = `
    @keyframes vt-lamp-pulse {
      0%,100% { box-shadow: 0 0 7px currentColor; }
      50%      { box-shadow: 0 0 14px currentColor, 0 0 24px rgba(16,185,129,0.28); }
    }

    .vt-dialog-card::before {
      content: '';
      position: absolute;
      inset: 0;
      border: 1.5px solid transparent;
      background: linear-gradient(135deg, rgba(128,24,231,0.6), rgba(16,185,129,0.5), rgba(59,130,246,0.55)) border-box;
      -webkit-mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
      -webkit-mask-composite: destination-out;
      mask-composite: exclude;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.4s;
    }
    .vt-dialog-card:hover::before { opacity: 1; }

    /* desktop — scrollable if content overflows */
    .vt-dialog-card {
      max-height: 90vh;
      overflow-y: auto;
    }

    /* mobile — fullscreen sheet */
    @media (max-width: 992px) {
      .vt-dialog-card {
        max-width: 100% !important;
        width: 100% !important;
        min-height: 90dvh !important;
        max-height: 90dvh !important;
        overflow-y: auto !important;
        border: none !important;
        border-radius: 0 !important;
        padding:0 !important;
        overflow-y: auto !important;
        align-self: flex-start;
      }
      .vt-dialog-card::before {
        display: none;
      }
    }

    .vt-close-btn:hover {
      color: rgba(255,255,255,1) !important;
      border-color: rgba(255,255,255,0.5) !important;
      background: rgba(255,255,255,0.06) !important;
    }
    .vt-footer-link:hover {
      color: rgba(255,255,255,0.85) !important;
      border-color: rgba(255,255,255,0.35) !important;
    }
  `;
  document.head.appendChild(el);
}

// ══════════════════════════════════════════════════════════════════════════════
// ContextNoteDialog
// ══════════════════════════════════════════════════════════════════════════════

const ContextNoteDialog = forwardRef(function ContextNoteDialog(
  { data = {}, open: controlledOpen, onClose },
  ref
) {
  injectStyles();

  const {
    title      = "Context Note",
    subtitle   = "",
    tags       = [],
    sections   = [],
    lamp       = "#10b981",
    brand      = "Vision Tools",
    storageKey = null,
  } = data;

  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  const [switchOn, setSwitchOn] = useState(() => {
    if (!storageKey) return true;
    return localStorage.getItem(storageKey) !== "false";
  });

  useImperativeHandle(ref, () => ({
    handleOpen:  () => setInternalOpen(true),
    handleClose: () => setInternalOpen(false),
    toggle:      () => setInternalOpen(p => !p),
  }));

  const handleClose = () => {
    if (isControlled) onClose?.();
    else setInternalOpen(false);
  };

  const handleToggle = () => {
    const next = !switchOn;
    setSwitchOn(next);
    if (storageKey) localStorage.setItem(storageKey, String(next));
  };

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === "Escape") handleClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="vt-backdrop"
          variants={backdropVariants}
          initial="hidden" animate="visible" exit="exit"
          onClick={handleClose}
          style={{
            position: "fixed", inset: 0, zIndex: 1300,
            background: "rgba(0,0,0,0.68)",
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "24px",
          }}
        >
          <motion.div
            key="vt-card"
            variants={cardVariants}
            initial="hidden" animate="visible" exit="exit"
            onClick={(e) => e.stopPropagation()}
            className="vt-dialog-card"
            style={gradBorderStyle}
          >

            {/* ── Top bar ── */}
            <div style={{
              display: "flex", alignItems: "center",
              justifyContent: "space-between", marginBottom: 20,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Lamp color={lamp} />
                <span style={{
                  fontSize: 10, fontWeight: 600, letterSpacing: "0.11em",
                  textTransform: "uppercase", color: "rgba(255,255,255,0.38)",
                  fontFamily: "'Barlow', sans-serif",
                }}>
                  Context note
                </span>
              </div>

              <button
                className="vt-close-btn"
                onClick={handleClose}
                style={{
                  background: "transparent",
                  border: "1px solid rgba(255,255,255,0.22)",
                  color: "rgba(255,255,255,0.65)",
                  cursor: "pointer", outline: "none",
                  width: 26, height: 26, padding: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "color 0.2s, border-color 0.2s, background 0.2s",
                }}
              >
                <CloseIcon sx={{ fontSize: 15 }} />
              </button>
            </div>

            {/* ── Title ── */}
            <div style={{
              fontSize: 19, fontWeight: 600,
              color: "rgba(255,255,255,0.95)",
              letterSpacing: "0.01em", lineHeight: 1.3,
              marginBottom: subtitle ? 5 : 16,
            }}>
              {title}
            </div>

            {/* ── Subtitle ── */}
            {subtitle && (
              <div style={{
                fontSize: 14, fontWeight: 300,
                color: "rgba(255,255,255,0.62)",
                letterSpacing: "0.04em", marginBottom: 18,
              }}>
                {subtitle}
              </div>
            )}

            {/* ── Divider ── */}
            <div style={{ height: 1, background: "rgba(255,255,255,0.09)", marginBottom: 18 }} />

            {/* ── Tags ── */}
            {tags.length > 0 && (
              <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 20 }}>
                {tags.map((t, i) => (
                  <Tag key={i} label={t.label} color={t.color} />
                ))}
              </div>
            )}

            {/* ── Sections ── */}
            {sections.map((s, i) => (
              <Section key={i} label={s.label}>
                <div
                  style={{
                    fontSize: 14.5, fontWeight: 300,
                    color: "rgba(255,255,255,0.78)",
                    lineHeight: 1.75,
                    fontFamily: "'Barlow', sans-serif",
                  }}
                  dangerouslySetInnerHTML={{ __html: s.body }}
                />
              </Section>
            ))}

            {/* ── Footer ── */}
            <div style={{
              display: "flex", alignItems: "center",
              justifyContent: "space-between",
              paddingTop: 14,
              borderTop: "1px solid rgba(255,255,255,0.07)",
              marginTop: 6,
            }}>
              <span style={{
                fontSize: 13, fontWeight: 500,
                letterSpacing: "0.1em", textTransform: "uppercase",
                opacity: 0.9,
              }}>
                <BrandText>{brand}</BrandText>
              </span>

              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>

                {/* ── Show on startup toggle ── */}
                {storageKey && (
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
                    onClick={handleToggle}
                  >
                    <span style={{
                      fontSize: 11, fontFamily: "'Barlow', sans-serif",
                      color: "rgba(255,255,255,0.32)", letterSpacing: "0.04em",
                      userSelect: "none",
                    }}>
                      Show on startup
                    </span>
                    <div style={{
                      width: 32, height: 18, borderRadius: 9,
                      background: switchOn ? "rgba(16,185,129,0.4)" : "rgba(255,255,255,0.08)",
                      border: switchOn ? "1px solid rgba(16,185,129,0.55)" : "1px solid rgba(255,255,255,0.14)",
                      position: "relative", flexShrink: 0,
                      transition: "background 0.25s, border-color 0.25s",
                    }}>
                      <span style={{
                        position: "absolute", top: 2,
                        left: switchOn ? 14 : 2,
                        width: 12, height: 12, borderRadius: "50%",
                        background: switchOn ? "#10b981" : "rgba(255,255,255,0.3)",
                        boxShadow: switchOn ? "0 0 6px rgba(16,185,129,0.7)" : "none",
                        transition: "left 0.25s, background 0.25s, box-shadow 0.25s",
                      }} />
                    </div>
                  </div>
                )}

                <button
                  className="vt-footer-link"
                  onClick={handleClose}
                  style={{
                    background: "transparent", border: "none", outline: "none",
                    cursor: "pointer", padding: 0,
                    fontSize: 12, fontWeight: 400,
                    color: "rgba(255,255,255,0.38)",
                    letterSpacing: "0.04em",
                    borderBottom: "1px solid rgba(255,255,255,0.15)",
                    fontFamily: "'Barlow', sans-serif",
                    transition: "color 0.2s, border-color 0.2s",
                  }}
                >
                  Dismiss
                </button>
              </div>
            </div>

          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
});

export default ContextNoteDialog;